import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  AdvancedPagination,
} from "@semo/shared-ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@semo/shared-ui";
import { Badge } from "@semo/shared-ui";
import { ScrollArea, ScrollBar } from "@semo/shared-ui";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
} from "@semo/icons";
import { useSupplierRequests } from "../items/application/hooks/useSupplierRequests";
import { useCategoryTree } from "../categories/hooks/useCategoryTree";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@semo/shared-ui";
import type { SupplierItemRequestSummaryDto } from "@semo/api-contracts";

interface SupplierRequestHistoryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const ITEMS_PER_PAGE = 10;

export const SupplierRequestHistoryDialog: React.FC<
  SupplierRequestHistoryDialogProps
> = ({ isOpen, onOpenChange }) => {
  const [page, setPage] = useState(1);
  const { requests, pagination, isLoading, isFetching } = useSupplierRequests({
    page,
    limit: ITEMS_PER_PAGE,
  });
  const { flatCategories } = useCategoryTree();

  const getCategoryName = (categoryId: string) => {
    const category = flatCategories.find((c) => c.id === categoryId);
    return category ? category.name : "-";
  };

  const getStatusBadge = (status: SupplierItemRequestSummaryDto["status"]) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200"
          >
            <Clock className="w-3 h-3 mr-1" /> 대기중
          </Badge>
        );
      case "approved":
        return (
          <Badge
            className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" /> 승인됨
          </Badge>
        );
      case "rejected":
        return (
          <Badge
            className="bg-red-100 text-red-800 hover:bg-red-200 border-red-200"
          >
            <XCircle className="w-3 h-3 mr-1" /> 반려됨
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalPages = pagination?.totalPages ?? 1;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        size="3xl"
        className="max-h-[90vh] flex flex-col p-0 overflow-hidden"
      >
        <DialogHeader className="p-6 pb-3">
          <DialogTitle className="text-lg font-semibold">
            나의 물품 요청 내역
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            관리자에게 요청한 신규 물품 등록 내역입니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden border-t relative">
          {(isLoading || isFetching) && (
            <div className="absolute inset-0 bg-white/50 z-20 flex items-center justify-center backdrop-blur-[1px]">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          )}

          {!isLoading && requests.length === 0 ? (
            <div className="h-96 flex flex-col items-center justify-center text-muted-foreground">
              <AlertCircle className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-lg">요청한 내역이 없습니다.</p>
            </div>
          ) : (
            <ScrollArea className="h-full max-h-[calc(90vh-220px)]">
              <div className="min-w-[1000px] p-6 pt-2">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                    <TableRow className="hover:bg-transparent border-b">
                      <TableHead className="w-[120px] text-xs font-medium text-slate-500">
                        상태
                      </TableHead>
                      <TableHead className="w-[220px] text-xs font-medium text-slate-500">
                        상품명
                      </TableHead>
                      <TableHead className="w-[150px] text-xs font-medium text-slate-500">
                        카테고리
                      </TableHead>
                      <TableHead className="w-[180px] text-xs font-medium text-slate-500">
                        브랜드/제조사
                      </TableHead>
                      <TableHead className="w-[120px] text-xs font-medium text-slate-500">
                        요청일
                      </TableHead>
                      <TableHead className="text-xs font-medium text-slate-500">
                        요청 사유
                      </TableHead>
                      <TableHead className="text-xs font-medium text-slate-500">
                        관리자 코멘트
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req: SupplierItemRequestSummaryDto) => (
                      <TableRow key={req.id} className="hover:bg-slate-50/50">
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        <TableCell className="font-semibold text-slate-900 py-4 text-sm">
                          {req.name}
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {getCategoryName(req.categoryId ?? "")}
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          <div className="flex flex-col gap-1">
                            {req.brand || req.manufacturer ? (
                              <div className="flex flex-col">
                                <span className="font-semibold text-slate-800">
                                  {req.brand}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {req.manufacturer}
                                </span>
                              </div>
                            ) : null}
                            <div className="text-2xs leading-tight text-slate-400">
                              {[req.spec1, req.spec2, req.spec3]
                                .filter(Boolean)
                                .join(" / ") || "-"}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {req.createdAt
                            ? format(new Date(req.createdAt), "yyyy-MM-dd", {
                                locale: ko,
                              })
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {req.requestReason ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="max-w-[180px] truncate text-sm text-slate-700 cursor-help underline decoration-dotted underline-offset-4 decoration-slate-300">
                                    {req.requestReason}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm p-3">
                                  <p className="break-keep leading-relaxed text-sm">
                                    {req.requestReason}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {req.adminComment ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="max-w-[180px] truncate text-sm text-red-600 font-medium cursor-help underline decoration-dotted underline-offset-4 decoration-red-200">
                                    {req.adminComment}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm p-3 border-red-100 bg-red-50 text-red-900">
                                  <p className="break-keep leading-relaxed text-sm">
                                    {req.adminComment}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-primary/15 bg-primary/5">
            <AdvancedPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
